import { useCallback, useEffect, useRef, useState } from "react";
import { Search, Loader2 } from "lucide-react";
import { api } from "../../lib/api";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { ClientCard } from "./ClientCard";

const FILTERS = [["todas", "Todas"], ["interacoes", "Com novas interações"], ["atencao", "Precisam de atenção"], ["novas", "Novas demandas"], ["urgentes", "Urgentes"], ["sla", "SLA em risco"],
  ["aguardando", "Aguardando cliente"], ["processamento", "Em processamento"], ["sem_pendencias", "Sem pendências"], ["implantacao", "Em implantação"]];
const SIZE = 24;

export function ClientCenter({ onSummary, base = "/backoffice/admin/clientes" }) {
  const [q, setQ] = useState("");
  const [dq, setDq] = useState("");
  const [filter, setFilter] = useState("todas");
  const [scope, setScope] = useState("all");
  const [data, setData] = useState(null);
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const shown = useRef(SIZE);
  useEffect(() => { const t = setTimeout(() => setDq(q), 300); return () => clearTimeout(t); }, [q]);
  const fetch = useCallback(async (page, size, append) => {
    setBusy(true);
    try {
      const { data: d } = await api.get("/admin/client-center", { params: { q: dq, filter, scope, page, size } });
      setData(d); onSummary && onSummary(d.summary);
      setItems((old) => (append ? [...old, ...d.items] : d.items));
    } finally { setBusy(false); }
  }, [dq, filter, scope, onSummary]);
  useEffect(() => { shown.current = SIZE; fetch(1, SIZE, false); }, [fetch]);
  useEffect(() => { const t = setInterval(() => fetch(1, Math.min(60, shown.current), false), 30000); return () => clearInterval(t); }, [fetch]);
  const more = () => { const page = Math.floor(items.length / SIZE) + 1; shown.current = items.length + SIZE; fetch(page, SIZE, true); };
  const c = data?.counts || {};
  return (
    <section className="space-y-4" data-testid="client-center">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div><h2 className="font-display text-2xl font-semibold">Clientes</h2><p className="text-sm text-slate-500">Quem precisa da nossa atenção aparece primeiro. Atualiza a cada 30 segundos.</p></div>
        <div className="flex rounded-full border bg-white p-1 text-xs" data-testid="client-scope">
          {[["all", "Todos os clientes"], ["mine", "Minha carteira"]].map(([k, l]) => <button key={k} onClick={() => setScope(k)} data-testid={`client-scope-${k}`} className={`px-3 py-1.5 rounded-full ${scope === k ? "bg-slate-900 text-white" : "text-slate-600"}`}>{l}</button>)}
        </div>
      </div>
      <div className="relative max-w-xl"><Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <Input className="pl-9 h-11 bg-white" placeholder="Buscar empresa, CNPJ, responsável..." value={q} onChange={(e) => setQ(e.target.value)} data-testid="client-search" />
        {busy && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-slate-400" />}</div>
      <div className="flex flex-wrap gap-2" data-testid="client-filters">
        {FILTERS.map(([k, l]) => <button key={k} onClick={() => setFilter(k)} data-testid={`client-filter-${k}`} className={`text-xs px-3 py-1.5 rounded-full border ${filter === k ? "bg-indigo-600 text-white border-indigo-600" : "bg-white hover:bg-slate-50"}`}>{l} <span className="font-mono opacity-70">{c[k] ?? ""}</span></button>)}
      </div>
      {data && !items.length && <div className="text-sm text-slate-500 border rounded-xl bg-white p-6 text-center" data-testid="client-empty">Nenhum cliente encontrado com esses critérios.</div>}
      <div className="grid sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-4">{items.map((x) => <ClientCard key={x.id} c={x} base={base} />)}</div>
      <div className="flex flex-wrap items-center gap-3">
        {data && items.length < data.total && <Button variant="outline" onClick={more} disabled={busy} data-testid="client-load-more">Carregar mais ({data.total - items.length})</Button>}
        {filter !== "sem_pendencias" && c.sem_pendencias > 0 && (
          <div className="text-sm text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-full px-4 py-1.5 flex items-center gap-3" data-testid="client-ok-banner">✓ {c.sem_pendencias} {c.sem_pendencias === 1 ? "cliente sem pendências" : "clientes sem pendências"}
            <button className="text-xs font-semibold underline" onClick={() => setFilter("sem_pendencias")} data-testid="client-ok-see-all">Ver todos</button></div>)}
      </div>
    </section>
  );
}
